import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import type { Client } from '@hero/schema';
import { resolveLoadPackingAndQuantityFromVehicles } from '@hero/core';
import { formatVehicleText } from '@hero/web-utils';
import { useAgreementListApi, useClientUpdateApi } from '@hero/api-hooks';
import { useClientWorkspaceStore } from '@hero/hooks-shared';
import { Price } from '@hero/web-components';
import { CompletenessBar, computeClientStatus } from '../../ClientPeekInfo';
import {
  EmptySectionHint,
  FieldDisplay,
  SectionCard,
} from '../../SectionCard';
import { EditableField } from '../../../../app/EditableField';
import { VehicleChip } from '../../../Vehicle/VehicleChip';
import { AgreementInfoSection } from './AgreementInfoSection';

export interface ClientInfoTabProps {
  client: Client;
}

/**
 * Workspace "Info" tab body. Renders the same section structure as
 * `ClientPage` but more compact (no outline rail; the workspace already
 * has tabs + hero). Wires inline edits for simple scalars (primary
 * contact, domain, AI prompt) and section modals (legal/settings/brand)
 * via `useClientWorkspaceStore.openSectionEdit`.
 */
export function ClientInfoTab({ client }: ClientInfoTabProps) {
  const { t } = useTranslation(['m-client', 'createOrder']);

  // Mirror `AgreementInfoSection`'s query — react-query dedupes on the
  // `(useAgreementListApi, { partyId })` cache key so this is the same
  // network request, not a duplicate. We need the result here too so
  // `computeClientStatus` can include the agreement section in the
  // overall % (per the role-aware allow-list in `ClientPeekInfo`).
  const agreementsListQuery = useAgreementListApi({ partyId: client._id });
  const hasOpenAgreement = useMemo<boolean | undefined>(() => {
    if (agreementsListQuery.isLoading || !agreementsListQuery.data) return undefined;
    return agreementsListQuery.data.some(
      a => a.status === 'new' || a.status === 'active',
    );
  }, [agreementsListQuery.data, agreementsListQuery.isLoading]);

  const status = computeClientStatus(client, { hasOpenAgreement });
  const openSectionEdit = useClientWorkspaceStore(s => s.openSectionEdit);

  const updateClient = useClientUpdateApi();

  const updateField = async (
    // `_id` is stamped here — callers pass only the changed fields.
    patch: Omit<Parameters<typeof updateClient.mutateAsync>[0], '_id'>,
  ) => {
    await updateClient.mutateAsync({ ...patch, _id: client._id });
  };

  // Render preferred vehicles as the same `VehicleChip` row used by
  // `RoutePriceCard` (agreement drawer) so the visual language for
  // "this vehicle class applies" stays identical across surfaces
  // instead of degrading to a comma-joined string here.
  const preferredVehicleTypes = client.settings?.vehicle_preferred_types;
  const hasPreferredVehicles =
    !!preferredVehicleTypes && preferredVehicleTypes.length > 0;
  const derivedPackingQuantity = useMemo(
    () =>
      resolveLoadPackingAndQuantityFromVehicles(preferredVehicleTypes ?? []),
    [preferredVehicleTypes],
  );
  const displayPacking =
    client.settings?.item_default_packing ?? derivedPackingQuantity.packing;
  const displayQuantity =
    client.settings?.item_default_quantity ?? derivedPackingQuantity.quantity;
  const preferredVehiclesNode = hasPreferredVehicles ? (
    <div className="flex flex-wrap gap-1.5">
      {preferredVehicleTypes!.map(v => (
        <VehicleChip key={v} vehicle={v} />
      ))}
    </div>
  ) : undefined;

  return (
    <div className="flex flex-col gap-4">
      {status.overall < 100 ? (
        <div className="px-1">
          <CompletenessBar percent={status.overall} />
        </div>
      ) : null}

      {/* Agreement — contract envelope for the (org, client) pair.
          Lives at the top of the Info tab because the contract is
          the highest-signal fact about a client (active vs not,
          payment cadence, expected volume, dates) — operators want
          to see it immediately instead of scrolling past primary
          contact / domain / legal / etc. The section owns its own
          data fetch + create/drawer modals (the backend invariant
          guarantees zero-or-one open agreement, so a single inline
          card / callout is enough). */}
      <AgreementInfoSection client={client} />

      {/* Primary contact (Required) — inline-editable scalars */}
      <SectionCard
        id="ws-section-contact"
        title={t('sections.primary_contact.title')}
        status={status.primaryContact}
        compact>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <EditableField
            label={t('page.fields.name')}
            value={client.primaryContact?.name}
            placeholder={t('inline_edit.placeholder.person_name')}
            onSave={next =>
              updateField({ primaryContact: { name: next } })
            }
            validate={v => (v.trim() ? null : t('form.person_name.validation'))}
          />
          <FieldDisplay
            label={t('page.fields.person_type')}
            value={client.primaryContact?.personType?.replace('_', ' ')}
          />
          {/* Phone is optional on the doc, so this field stays editable
              but is not validated as required — a client added for its CR
              alone has no number to enter yet. */}
          <EditableField
            label={t('page.fields.phone')}
            value={client.primaryContact?.phone}
            variant="phone"
            placeholder={t('inline_edit.placeholder.phone')}
            onSave={next =>
              updateField({ primaryContact: { phone: next || undefined } })
            }
          />
          <EditableField
            label={t('page.fields.email')}
            value={client.primaryContact?.email}
            variant="email"
            placeholder={t('inline_edit.placeholder.email')}
            onSave={next =>
              updateField({ primaryContact: { email: next || undefined } })
            }
          />
        </div>
      </SectionCard>

      {/*
        Brand link is intentionally hidden in the workspace Info tab
        for now. The brand-link flow needs its own UX pass (Profile
        directory picker, unlink confirmation, conflict handling). Until
        then we keep the data on the model but don't expose the editor.
        Re-enable by restoring the SectionCard and re-adding 'brand' to
        the editingSection tabs in `useClientWorkspaceStore`.
      */}

      {/* Domain — inline-editable scalar. Empty string clears the
          field; the schema (`domainSchema`) extracts a clean host from
          any URL-ish input the user pastes (`https://www.foo.com/bar`
          → `foo.com`). */}
      <SectionCard
        id="ws-section-domain"
        title={t('sections.domain.title')}
        status={status.domain}
        compact>
        <EditableField
          label={t('page.fields.domain')}
          value={client.domain}
          placeholder={t('inline_edit.placeholder.domain')}
          emptyDisplay={t('drawer.peek.empty_states.no_domain_set')}
          onSave={next =>
            updateField({ domain: next.trim() ? next.trim() : undefined })
          }
        />
      </SectionCard>

      {/* Legal — sub-object via section modal */}
      <SectionCard
        id="ws-section-legal"
        title={t('sections.legal.title')}
        status={status.legal}
        compact
        onEdit={() => openSectionEdit('legal')}>
        {status.legal.variant === 'empty' ? (
          <EmptySectionHint
            label={t('drawer.peek.empty_states.no_legal_documents')}
            onAdd={() => openSectionEdit('legal')}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldDisplay
              label={t('page.fields.cr_number')}
              value={client.legal?.registrationId}
              empty="—"
            />
            <FieldDisplay
              label={t('page.fields.cr_name')}
              value={client.legal?.registrationName}
              empty="—"
            />
            <FieldDisplay
              label={t('page.fields.tax_id')}
              value={client.legal?.taxId}
              empty="—"
            />
            <FieldDisplay
              label={t('page.fields.license_id')}
              value={client.legal?.licenseId}
              empty="—"
            />
            <FieldDisplay
              label={t('page.fields.address')}
              value={client.legal?.fullAddress}
              empty="—"
            />
            <FieldDisplay
              label={t('page.fields.city')}
              value={
                client.legal?.addressCity
                  ? `${client.legal.addressCity}${
                      client.legal.addressCountry
                        ? `, ${client.legal.addressCountry}`
                        : ''
                    }`
                  : undefined
              }
              empty="—"
            />
          </div>
        )}
      </SectionCard>

      {/* Order settings (Client.settings) — sub-object via section modal */}
      <SectionCard
        id="ws-section-settings"
        title={t('sections.order_defaults.title')}
        status={status.settings}
        compact
        onEdit={() => openSectionEdit('settings')}>
        {status.settings.variant === 'empty' ? (
          <EmptySectionHint
            label={t('drawer.peek.empty_states.no_order_defaults')}
            onAdd={() => openSectionEdit('settings')}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldDisplay
              label={t('page.fields.default_item')}
              value={client.settings?.item_default_name}
              empty="—"
            />
            <FieldDisplay
              label={t('page.fields.default_item_type')}
              value={
                client.settings?.item_default_type
                  ? t(
                      `m-order:item_types.${client.settings.item_default_type}`,
                      client.settings.item_default_type,
                    )
                  : undefined
              }
              empty="—"
            />
            <FieldDisplay
              label={t('page.fields.default_item_cost')}
              value={
                client.settings?.item_default_cost != null ? (
                  <Price amount={client.settings.item_default_cost} currency="SAR" />
                ) : undefined
              }
              empty="—"
            />
            <FieldDisplay
              label={t('page.fields.default_packing')}
              value={displayPacking ? formatVehicleText(displayPacking) : undefined}
              empty="—"
            />
            <FieldDisplay
              label={t('page.fields.default_quantity')}
              value={displayQuantity != null ? String(displayQuantity) : undefined}
              empty="—"
            />
            <FieldDisplay
              label={t('page.fields.default_weight')}
              value={
                client.settings?.item_default_weight
                  ? `${client.settings.item_default_weight.value} ${client.settings.item_default_weight.type}`
                  : undefined
              }
              empty="—"
            />
            <FieldDisplay
              label={t('page.fields.preferred_vehicles')}
              value={preferredVehiclesNode}
              empty="—"
            />
            <FieldDisplay
              label={t('page.fields.pod_settings')}
              value={
                client.settings?.pod_settings === 'return_original_pod'
                  ? t('edit_modal.field.pod_settings.option_return_original_pod')
                  : client.settings?.pod_settings === 'pod_image'
                    ? t('edit_modal.field.pod_settings.option_pod_image')
                    : undefined
              }
              empty="—"
            />
            {/* Financial / operations report recipients are
                intentionally hidden from the workspace Info tab for
                now. They have no edit affordance in the
                `EditClientSettingsModal` yet (which only edits cargo
                / weight / vehicle preferences), so always rendering
                them as "—" just adds noise. Re-introduce both rows
                once the recipients picker UX lands. */}
          </div>
        )}
      </SectionCard>

      {/* AI instructions — inline-editable textarea */}
      <SectionCard
        id="ws-section-ai"
        title={t('sections.ai_instructions.title')}
        status={status.ai}
        compact>
        <EditableField
          label={t('page.fields.ai_skill')}
          value={client.skill}
          variant="textarea"
          placeholder={t('inline_edit.placeholder.skill')}
          emptyDisplay={t('drawer.peek.empty_states.no_ai_instructions')}
          onSave={next => updateField({ skill: next })}
        />
      </SectionCard>

      {/* Activity is intentionally omitted — `lastOrderDate` is a
          side-effect of operations, not a setup step, so showing
          "no orders yet" pressured operators to chase a value they
          can't actually fill in. The hero band still surfaces order
          stats elsewhere when we need them. */}
    </div>
  );
}
